import { For, type JSX, Show } from 'solid-js';
import { t } from '../i18n';
import { ApiError } from '../lib/api';
import { kbRetrievedLabel, useKbPage } from '../lib/kb';
import { absolute } from '../lib/time';
import { Panel } from './Panel';
import { QueryBoundary } from './QueryBoundary';
import { Skeleton } from './Skeleton';
import { Button } from './ui/Button';
import { MarkdownWidget } from './widgets/MarkdownWidget';

// Repo KB tab → one page, verbatim (`GET /v1/kb/:repoId/pages/:slug`). Container:
// it owns the point read for whichever slug the index or a search hit opened.
//
// A 404 IS AN ANSWER, not a fault — the page was consolidated away by the
// gardener, or a cross-link points at a page that was never written. So it
// renders as its own line rather than through the boundary's error box, whose
// Retry would 404 forever (the `RepoPendingTab` / task-drawer rule). Every other
// failure keeps the retryable box: "ask again" must not read as "no such page".
//
// THE BODY IS UNTRUSTED. It is written by agents (`kb_page_upsert`) and may
// carry anything the repo's own prose does, so it goes through the one
// sanitize-then-innerHTML seam — <MarkdownWidget/> over `renderMarkdown` — and
// never near a raw `innerHTML` of its own.
//
// `updated_by_run_id` is a caller-declared LABEL, not an address: nothing joins
// or resolves that column (`apps/api/src/routes/kb.ts` pins the contract), so it
// is shown as text and deliberately NOT linked to a run view. The day it becomes
// a link is the day the API has to fence it, in that same change.

export function KbPagePanel(props: {
  readonly repoId: string;
  readonly slug: string;
  readonly token: string | null;
  readonly onOpen: (slug: string) => void;
  readonly onClose: () => void;
}): JSX.Element {
  const page = useKbPage(
    () => props.repoId,
    () => props.slug,
    props.token,
  );
  const gone = (): boolean =>
    page.isError && page.error instanceof ApiError && page.error.status === 404;

  return (
    <Panel
      title={props.slug}
      action={
        <Button variant="ghost" size="sm" onClick={() => props.onClose()}>
          {t('repo_kb.close')}
        </Button>
      }
    >
      <Show when={!gone()} fallback={<p class="list-empty">{t('repo_kb.page_gone')}</p>}>
        <QueryBoundary query={page} loading={<Skeleton height="12rem" />}>
          {(p) => (
            <div class="repo-kb__page">
              <MarkdownWidget content={p.text} />

              <Show when={p.links.length > 0}>
                <div class="repo-kb__links">
                  <span class="repo-kb__meta">{t('repo_kb.links')}</span>
                  <For each={p.links}>
                    {(link) => (
                      <Button variant="outline" size="sm" onClick={() => props.onOpen(link)}>
                        {link}
                      </Button>
                    )}
                  </For>
                </div>
              </Show>

              <dl class="facts facts--rows">
                <div>
                  <dt>{t('repo_kb.fact_updated')}</dt>
                  <dd>{absolute(p.ts)}</dd>
                </div>
                <div>
                  <dt>{t('repo_kb.fact_retrieved')}</dt>
                  <dd>
                    {kbRetrievedLabel(p.used_count)}
                    {p.last_used_at
                      ? ` · ${absolute(p.last_used_at)}`
                      : ` · ${t('repo_kb.never_retrieved')}`}
                  </dd>
                </div>
                <div>
                  <dt>{t('repo_kb.fact_run')}</dt>
                  <dd>
                    <code>{p.updated_by_run_id ?? t('repo_kb.no_run')}</code>
                  </dd>
                </div>
              </dl>
            </div>
          )}
        </QueryBoundary>
      </Show>
    </Panel>
  );
}
